import { Component } from '@theme/component';

const mobileQuery = window.matchMedia('(max-width: 749px)');
const finePointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

/** localStorage key for the variants this browser has already requested: `{ [variantId]: requestedAt }`. */
const REQUESTED_KEY = 'notify-me:requested';
const REQUESTED_MAX_AGE = 30 * 24 * 60 * 60 * 1000;

/** @returns {Record<string, number>} Requests from the last 30 days. */
function readRequested() {
  try {
    /** @type {Record<string, number>} */
    const all = JSON.parse(localStorage.getItem(REQUESTED_KEY) ?? '{}');
    const cutoff = Date.now() - REQUESTED_MAX_AGE;
    return Object.fromEntries(Object.entries(all).filter(([, at]) => at > cutoff));
  } catch {
    return {};
  }
}

/** @param {string} variantId */
function markRequested(variantId) {
  try {
    localStorage.setItem(REQUESTED_KEY, JSON.stringify({ ...readRequested(), [variantId]: Date.now() }));
  } catch {
    // Not remembered; the button just stays as "Notify me".
  }
}

/**
 * "Notify me" form for out-of-stock variants and variants flagged with the `custom.notify_me` metafield.
 *
 * Sends a Shopify contact form submission (emailed to the store admin) whose body is a fixed, parsable block:
 *
 *   NOTIFY_ME_REQUEST v1
 *   sku=KTL-IP18P-LROS-BLK
 *   variant_id=50464219693104
 *   ...
 *   END
 *
 * Keep the keys and their order stable; bump the version if the format changes.
 *
 * The request is sent in the background so the page (and the selected variant) stays as it is. If the customer opts
 * in to marketing, a second background submit adds them as a subscribed customer tagged with the requested SKU.
 *
 * Requested variants are remembered in localStorage for 30 days; for those, the "Notify me" button is disabled and
 * reads "You're on the list". Shopify rejects repeat contact submissions from the same visitor, so this also stops
 * customers from running into that.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} panel - The form panel (floating popover or inline, see `data-layout`).
 * @property {HTMLInputElement} bodyInput - Hidden `contact[body]` input.
 * @property {HTMLInputElement} emailInput - Email input.
 * @property {HTMLInputElement} phoneInput - Phone input.
 * @property {HTMLInputElement} optInInput - Marketing opt-in checkbox.
 * @property {HTMLElement} variantTitle - Shows the selected variant's title.
 * @property {HTMLElement} success - Success message.
 * @property {HTMLElement} error - Error message container.
 * @property {HTMLElement} errorText - Error message text.
 * @property {HTMLButtonElement} submitButton - Submit button.
 * @property {HTMLElement} requestedNote - Shown under the button for variants that were already requested.
 *
 * @extends Component<Refs>
 */
class NotifyMeComponent extends Component {
  requiredRefs = ['panel', 'bodyInput', 'emailInput', 'phoneInput', 'optInInput', 'submitButton'];

  /** @type {DOMStringMap} */
  #variant = {};

  connectedCallback() {
    super.connectedCallback();

    if (this.form) {
      // Validation is handled in #handleSubmit so email and phone can each be optional.
      this.form.noValidate = true;
      this.form.addEventListener('submit', this.#handleSubmit);
    }

    const button = this.closest('.buy-buttons-block')?.querySelector('product-form-component [ref="addToCartButton"]');
    if (button instanceof HTMLElement) this.#applyRequestedState(button);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.form?.removeEventListener('submit', this.#handleSubmit);
    this.#removeDismissListeners();
  }

  /**
   * Whether the form should float as a popover. Mobile always uses the inline layout: a fixed popover ends up behind
   * the on-screen keyboard, while an in-flow form lets the browser scroll the focused field into view.
   */
  get isPopover() {
    return this.dataset.layout !== 'inline' && !mobileQuery.matches;
  }

  /** @returns {HTMLFormElement | null} */
  get form() {
    return this.querySelector('form.notify-me__form');
  }

  /**
   * Toggles the form for the variant described by the "Notify me" button's data attributes.
   * @param {HTMLElement} button - The "Notify me" button.
   */
  open(button) {
    if (this.#isRequested(button)) return;

    if (this.isOpen) {
      this.close();
      return;
    }

    this.#loadVariant(button);
    this.#openPanel();
  }

  /**
   * Called by product-form.js after the variant changes. Keeps an open form on the new variant, or closes it if the
   * new variant isn't a "Notify me" variant.
   * @param {HTMLElement} button - The updated add-to-cart button.
   */
  sync(button) {
    this.#applyRequestedState(button);

    // The variant update re-renders the section, which resets the panel to hidden, so rely on our own flag.
    if (!this.#isOpen) return;

    if (button.dataset.notifyMe !== 'true' || this.#isRequested(button)) {
      this.close();
      return;
    }

    this.#loadVariant(button);
    this.#openPanel({ reveal: false });
  }

  #isOpen = false;

  /** @param {HTMLElement} button */
  #isRequested(button) {
    const { variantId } = button.dataset;
    return button.dataset.notifyMe === 'true' && !!variantId && variantId in readRequested();
  }

  /**
   * Shows "You're on the list" on the "Notify me" button for variants this browser already requested. Other variants
   * keep the server-rendered button (product-form.js re-renders it on every variant change).
   * @param {HTMLElement} button - The add-to-cart button.
   */
  #applyRequestedState(button) {
    const requested = this.#isRequested(button);
    this.refs.requestedNote.hidden = !requested;
    if (!requested || !(button instanceof HTMLButtonElement)) return;

    button.disabled = true;
    const label = button.querySelector('.add-to-cart-text__content span span');
    if (label) label.textContent = this.dataset.requestedLabel ?? '';
  }

  /** @param {HTMLElement} button */
  #loadVariant(button) {
    this.#variant = { ...button.dataset };
    this.#trigger = button;

    const { variantTitle, success, error } = this.refs;
    if (variantTitle) variantTitle.textContent = this.#variant.variantTitle ?? '';

    // A different variant means a fresh request.
    if (this.form?.hidden && this.dataset.submittedVariant !== this.#variant.variantId) {
      this.form.hidden = false;
      if (success) success.hidden = true;
    }
    if (error) error.hidden = true;
  }

  get isOpen() {
    return this.#isOpen;
  }

  close = () => {
    const { panel } = this.refs;

    if (panel.matches('[popover]:popover-open')) panel.hidePopover();
    panel.hidden = true;
    panel.removeAttribute('popover');
    panel.style.removeProperty('top');
    panel.style.removeProperty('left');
    panel.style.removeProperty('width');

    this.#isOpen = false;
    this.#trigger?.setAttribute('aria-expanded', 'false');
    this.#removeDismissListeners();
  };

  /** @type {HTMLElement | undefined} */
  #trigger;

  /**
   * @param {{ reveal?: boolean }} [options] - `reveal: false` keeps the scroll position and focus as they are.
   */
  #openPanel({ reveal = true } = {}) {
    const { panel, emailInput } = this.refs;
    this.#trigger ??= this.closest('.buy-buttons-block')?.querySelector('[data-notify-me="true"]') ?? undefined;

    panel.hidden = false;
    this.#isOpen = true;
    this.#removeDismissListeners();

    if (this.isPopover) {
      panel.popover = 'manual';
      if (!panel.matches(':popover-open')) panel.showPopover();
      this.#position();
      window.addEventListener('scroll', this.#schedulePosition, { passive: true });
      window.addEventListener('resize', this.#schedulePosition);
      // Defer so the click that opened the popover doesn't immediately close it.
      setTimeout(() => {
        if (!this.isOpen) return;
        document.addEventListener('click', this.#handleOutsideClick);
        document.addEventListener('keydown', this.#handleKeyDown);
      });
    } else if (reveal) {
      panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }

    // Switching between mobile and desktop while open would leave the form in the wrong layout.
    mobileQuery.addEventListener('change', this.close, { once: true });

    this.#trigger?.setAttribute('aria-expanded', 'true');

    // On touch devices, focusing would open the keyboard over the form before the customer has seen it.
    if (reveal && !this.form?.hidden && finePointerQuery.matches) emailInput.focus({ preventScroll: true });
  }

  #removeDismissListeners() {
    document.removeEventListener('click', this.#handleOutsideClick);
    document.removeEventListener('keydown', this.#handleKeyDown);
    window.removeEventListener('scroll', this.#schedulePosition);
    window.removeEventListener('resize', this.#schedulePosition);
    mobileQuery.removeEventListener('change', this.close);
  }

  #positionFrame = 0;

  #schedulePosition = () => {
    cancelAnimationFrame(this.#positionFrame);
    this.#positionFrame = requestAnimationFrame(() => this.#position());
  };

  /**
   * Places the popover just below the buy buttons row, matching its width. Flips above if there's no room below.
   */
  #position() {
    const { panel } = this.refs;
    const anchor = this.#trigger?.closest('.product-form-buttons') ?? this.#trigger;
    if (!anchor) return;

    const gap = 8;
    const rect = anchor.getBoundingClientRect();
    const width = Math.min(rect.width, window.innerWidth - gap * 2);
    const left = Math.min(Math.max(rect.left, gap), window.innerWidth - width - gap);

    panel.style.width = `${width}px`;
    panel.style.left = `${left}px`;

    const height = panel.offsetHeight;
    const fitsBelow = rect.bottom + gap + height <= window.innerHeight;
    const fitsAbove = rect.top - gap - height >= 0;
    const top = !fitsBelow && fitsAbove ? rect.top - gap - height : rect.bottom + gap;

    panel.style.top = `${Math.max(gap, top)}px`;
  }

  /** @param {MouseEvent} event */
  #handleOutsideClick = (event) => {
    const target = /** @type {Node} */ (event.target);
    if (this.refs.panel.contains(target) || this.#trigger?.contains(target)) return;
    this.close();
  };

  /** @param {KeyboardEvent} event */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    this.close();
    this.#trigger?.focus();
  };

  /** @param {SubmitEvent} event */
  #handleSubmit = async (event) => {
    event.preventDefault();

    const form = this.form;
    if (!form) return;

    const { emailInput, phoneInput, optInInput, submitButton, bodyInput } = this.refs;
    const email = emailInput.value.trim();
    const phone = phoneInput.value.trim();
    const optIn = optInInput.checked;

    if (!email && !phone) return this.#showError(this.dataset.errorMissingContact, emailInput);
    if (email && !emailInput.checkValidity()) return this.#showError(this.dataset.errorInvalidEmail, emailInput);
    if (optIn && !email) return this.#showError(this.dataset.errorOptInEmail, emailInput);

    this.refs.error.hidden = true;
    bodyInput.value = this.#buildBody({ email, phone, optIn });

    const formData = new FormData(form);
    // Shopify's contact form requires an email. For phone-only requests, send it from the store's own address;
    // the customer's (empty) email is recorded in the message body.
    if (!email) formData.set('contact[email]', this.dataset.fallbackEmail ?? '');

    submitButton.disabled = true;
    submitButton.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(form.action, { method: 'POST', body: formData });

      if (!response.ok || !response.url.includes('contact_posted=true')) throw new Error('Contact form rejected');

      if (optIn) await this.#subscribe(form.action, email);

      this.dataset.submittedVariant = this.#variant.variantId ?? '';
      if (this.#variant.variantId) markRequested(this.#variant.variantId);
      this.#showSuccess();
      if (this.#trigger) this.#applyRequestedState(this.#trigger);
    } catch {
      this.#showError(this.dataset.errorGeneric);
    } finally {
      submitButton.disabled = false;
      submitButton.removeAttribute('aria-busy');
    }
  };

  /**
   * Adds the customer as a marketing subscriber, tagged with the requested SKU. Failures are ignored: the admin
   * email (which records `marketing_opt_in=true`) is the source of truth.
   * @param {string} action - The form endpoint.
   * @param {string} email - The customer's email.
   */
  async #subscribe(action, email) {
    const tags = ['newsletter', 'notify-me'];
    if (this.#variant.sku) tags.push(`notify-me:${this.#variant.sku}`);

    const data = new FormData();
    data.set('form_type', 'customer');
    data.set('utf8', '✓');
    data.set('contact[email]', email);
    data.set('contact[tags]', tags.join(','));

    try {
      await fetch(action, { method: 'POST', body: data });
    } catch {
      // Ignored, see above.
    }
  }

  /**
   * @param {{ email: string, phone: string, optIn: boolean }} contact
   * @returns {string}
   */
  #buildBody({ email, phone, optIn }) {
    /** @type {Record<string, string>} */
    let options = {};
    try {
      options = JSON.parse(this.#variant.options ?? '{}');
    } catch {
      // Leave options empty.
    }

    const variantUrl = new URL(window.location.href);
    variantUrl.search = '';
    variantUrl.hash = '';
    if (this.#variant.variantId) variantUrl.searchParams.set('variant', this.#variant.variantId);

    /** @type {[string, string | undefined][]} */
    const fields = [
      ['sku', this.#variant.sku],
      ['variant_id', this.#variant.variantId],
      ['product_handle', this.dataset.productHandle],
      ...Object.entries(options),
      ['email', email],
      ['phone', phone],
      ['marketing_opt_in', String(optIn)],
      ['page_url', variantUrl.toString()],
      ['submitted_at', new Date().toISOString().replace(/\.\d{3}Z$/, 'Z')],
    ];

    const clean = (/** @type {string | undefined} */ value) => (value ?? '').replace(/[\r\n]+/g, ' ').trim();

    return ['NOTIFY_ME_REQUEST v1', ...fields.map(([key, value]) => `${key}=${clean(value)}`), 'END'].join('\n');
  }

  #showSuccess() {
    const { success } = this.refs;
    if (this.form) this.form.hidden = true;
    if (success) success.hidden = false;
    if (this.refs.panel.matches('[popover]:popover-open')) this.#schedulePosition();
  }

  /**
   * @param {string | undefined} message
   * @param {HTMLInputElement} [focusTarget]
   */
  #showError(message, focusTarget) {
    const { error, errorText } = this.refs;
    if (errorText) errorText.textContent = message ?? '';
    if (error) error.hidden = false;
    focusTarget?.focus();
  }
}

if (!customElements.get('notify-me-component')) customElements.define('notify-me-component', NotifyMeComponent);
